import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import RefreshIcon from '@mui/icons-material/Refresh'
import CheckIcon from '@mui/icons-material/CheckCircleOutlined'
import ExpiredIcon from '@mui/icons-material/HighlightOffOutlined'
import KeyIcon from '@mui/icons-material/KeyOutlined'
import MailIcon from '@mui/icons-material/MailOutlined'
import SwitchIcon from '@mui/icons-material/SwapHorizOutlined'
import RefreshTokenIcon from '@mui/icons-material/AutorenewOutlined'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'

import { makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { tokens } from '../theme'

type SessionRow = {
  event_id: string
  tenant_id: string
  tenant_slug: string
  user_id: string
  user_email: string
  role: string
  event_kind: 'login_password' | 'login_otp' | 'switch' | 'refresh'
  issued_at: string
  expires_at: string
  client_ip?: string | null
  user_agent?: string | null
  still_valid: boolean
}

type TenantRow = { tenant_id: string; slug: string; display_name: string; is_platform: boolean }

const WINDOWS: Array<{ label: string; hours: number }> = [
  { label: 'Last hour',    hours: 1   },
  { label: 'Last 24 h',    hours: 24  },
  { label: 'Last 7 days',  hours: 168 },
  { label: 'Last 30 days', hours: 720 },
]

const KINDS: Array<{ value: SessionRow['event_kind'] | 'all'; label: string; icon: React.ReactNode; color: string }> = [
  { value: 'all',            label: 'All events',       icon: <RefreshIcon fontSize="small" />,      color: 'text.secondary' },
  { value: 'login_password', label: 'Password login',   icon: <KeyIcon fontSize="small" />,          color: tokens.indigo },
  { value: 'login_otp',      label: 'OTP login',        icon: <MailIcon fontSize="small" />,         color: tokens.gold },
  { value: 'switch',         label: 'Workspace switch', icon: <SwitchIcon fontSize="small" />,       color: '#5B9CF6' },
  { value: 'refresh',        label: 'Token refresh',    icon: <RefreshTokenIcon fontSize="small" />, color: '#22C55E' },
]

export default function PlatformSessionsPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])

  const [windowHours, setWindowHours] = useState(24)
  const [emailQ, setEmailQ] = useState('')
  const [kind, setKind] = useState<(typeof KINDS)[number]['value']>('all')
  const [tenantIdQ, setTenantIdQ] = useState('')

  const tenantsQ = useQuery({
    queryKey: ['platform', 'tenants'] as const,
    queryFn: () => client.call<TenantRow[]>('/api/v1/platform/tenants'),
    staleTime: 60_000,
  })

  const sinceISO = useMemo(() => {
    const d = new Date()
    d.setHours(d.getHours() - windowHours)
    return d.toISOString()
  }, [windowHours])

  const sessionsQ = useQuery({
    queryKey: ['platform', 'sessions', windowHours, emailQ, kind, tenantIdQ] as const,
    queryFn: async () => {
      const params = new URLSearchParams({ since: sinceISO, limit: '500' })
      if (emailQ.trim())      params.set('email', emailQ.trim())
      if (kind !== 'all')     params.set('kind', kind)
      if (tenantIdQ)          params.set('tenant_id', tenantIdQ)
      return client.call<SessionRow[]>(`/api/v1/platform/sessions?${params.toString()}`)
    },
  })

  const rows = sessionsQ.data ?? []
  const activeCount = rows.filter((r) => r.still_valid).length

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
              Sessions
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Every token issued in the selected window · {rows.length} events · {activeCount} still within TTL
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <IconButton onClick={() => sessionsQ.refetch()} disabled={sessionsQ.isFetching}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Filters */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select size="small" label="Window" value={windowHours}
              onChange={(e) => setWindowHours(Number(e.target.value))}
              sx={{ minWidth: 160 }}
            >
              {WINDOWS.map((w) => <MenuItem key={w.hours} value={w.hours}>{w.label}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="Event kind" value={kind}
              onChange={(e) => setKind(e.target.value as (typeof KINDS)[number]['value'])}
              sx={{ minWidth: 200 }}
            >
              {KINDS.map((k) => (
                <MenuItem key={k.value} value={k.value}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Box sx={{ color: k.color, display: 'flex' }}>{k.icon}</Box>
                    <span>{k.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" label="Tenant" value={tenantIdQ}
              onChange={(e) => setTenantIdQ(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">All tenants</MenuItem>
              {(tenantsQ.data ?? []).map((t) => (
                <MenuItem key={t.tenant_id} value={t.tenant_id}>
                  {t.is_platform ? 'Platform' : t.display_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" label="Email contains" value={emailQ}
              onChange={(e) => setEmailQ(e.target.value)}
              sx={{ minWidth: 220 }}
              placeholder="e.g. @acme-water"
            />
          </Stack>
        </Paper>

        {sessionsQ.isLoading && <LinearProgress />}
        {sessionsQ.isError && (
          <Alert severity="error">
            {sessionsQ.error instanceof Error ? sessionsQ.error.message : 'Failed to load sessions.'}
          </Alert>
        )}

        {sessionsQ.data && (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>User-agent</TableCell>
                    <TableCell align="center">TTL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
                        No session events in this window.
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => {
                    const meta = KINDS.find((k) => k.value === r.event_kind) ?? KINDS[0]
                    return (
                      <TableRow key={r.event_id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {new Date(r.issued_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {new Date(r.issued_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Box sx={{ color: meta.color, display: 'flex' }}>{meta.icon}</Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{meta.label}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.user_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.tenant_slug}
                            sx={{
                              fontFamily: 'ui-monospace, monospace', fontSize: 11, height: 20,
                              bgcolor: r.tenant_slug === '__platform' ? alpha(tokens.gold, 0.16) : alpha(tokens.indigo, 0.10),
                              color:   r.tenant_slug === '__platform' ? tokens.gold : tokens.indigo,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                            {r.role.replace(/_/g, ' ')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                            {r.client_ip ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={r.user_agent ?? ''}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 200, display: 'block' }} noWrap>
                              {shortenUA(r.user_agent ?? '')}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          {r.still_valid ? (
                            <Tooltip title={`Expires ${new Date(r.expires_at).toLocaleString('en-GB')}`}>
                              <CheckIcon sx={{ color: '#22C55E', fontSize: 18 }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title={`Expired ${new Date(r.expires_at).toLocaleString('en-GB')}`}>
                              <ExpiredIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>
    </Container>
  )
}

// shortenUA collapses a full user-agent to something readable — the
// first mainstream browser + platform token. Full UA is on the tooltip.
function shortenUA(ua: string): string {
  if (!ua) return '—'
  const m = ua.match(/(Chrome|Firefox|Safari|Edg|OPR)\/([\d.]+)/)
  const os =
    /Windows NT/.test(ua) ? 'Windows' :
    /Mac OS X/.test(ua)   ? 'macOS' :
    /Android/.test(ua)    ? 'Android' :
    /iPhone|iPad/.test(ua)? 'iOS' :
    /Linux/.test(ua)      ? 'Linux' : ''
  if (m) return `${m[1]} ${m[2].split('.')[0]}${os ? ' · ' + os : ''}`
  return ua.slice(0, 40)
}
